import { canPerform } from '@aegislab/shared';
import { useMediaQuery } from '@/shared/lib/useMediaQuery';
import { mobile } from '@/shared/ui/theme';
import { SegmentedControl } from '@/shared/ui/SegmentedControl';
import { useAuthStore } from '@/shared/model';
import { useBugReports } from '../../model/useBugReports';
import { useBugReportViewStore, BUG_REPORT_VIEW_TABS } from '../../model/bugReportViewStore';
import * as s from './BugReportViewTabs.css';

export interface BugReportViewTabsProps {
  /** 창 제목. `titleSlot` 은 title 문자열을 **대체**하므로 여기서 함께 그린다. */
  title: string;
}

/**
 * 제보 · 요청 창의 타이틀바 슬롯 — 제목 + 뷰 토글(목록/통계) + 목록 건수.
 *
 * 본문 상단에 있던 토글 줄을 걷어내고 이미 비어 있던 타이틀바로 올린 것이다. 그 줄은
 * 창 폭의 20% 만 쓰면서 세로 45px 을 가져가고 있었다. 상태는 `useBugReportViewStore`
 * 로 나르고 렌더 소유권은 `BugReportPage` 에 그대로 둔다.
 *
 * 모바일에서는 아무것도 덧대지 않는다 — `MobileWindowTakeover` 가 슬롯을 `<h2>` 안에
 * 넣어서 tablist 를 제목에 중첩하게 되고, 애초에 상세가 전체 화면이라 줄 하나가 아깝지
 * 않다. 그쪽 토글은 `BugReportPage` 가 기존대로 본문에 그린다.
 */
export function BugReportViewTabs({ title }: BugReportViewTabsProps) {
  const isDesktop = !useMediaQuery(mobile);
  const user = useAuthStore((st) => st.user);
  const activeTab = useBugReportViewStore((st) => st.activeTab);
  const setActiveTab = useBugReportViewStore((st) => st.setActiveTab);
  const { data, hasNextPage } = useBugReports();

  // 트리아저(분류·처리 권한자) 판정 — 백엔드 게이트와 동일한 역할 축이다.
  const isTriager = !!user && canPerform(user, 'bug-report', 'triage');

  if (!isDesktop) return <>{title}</>;

  const count = data?.pages.reduce((n, page) => n + page.data.length, 0) ?? 0;
  const countLabel = `${count}건${hasNextPage ? '+' : ''}`;
  const showStats = isTriager && activeTab === 'stats';

  return (
    <span className={s.root}>
      <span className={s.title}>{title}</span>
      {isTriager && (
        // 타이틀바 전체가 드래그 핸들이고 더블클릭은 최대화다. 이 세 이벤트를 끊지 않으면
        // 탭을 누를 때 창이 끌리거나 최대화된다 — DesktopWindow 의 windowControls 와 같은 처방.
        <span
          className={s.tabs}
          role="presentation"
          onPointerDown={(e) => e.stopPropagation()}
          onMouseDown={(e) => e.stopPropagation()}
          onDoubleClick={(e) => e.stopPropagation()}
        >
          <SegmentedControl
            segments={BUG_REPORT_VIEW_TABS}
            activeId={activeTab}
            onSelect={setActiveTab}
            ariaLabel="제보 · 요청 보기 전환"
          />
        </span>
      )}
      {!showStats && <span className={s.count}>{countLabel}</span>}
    </span>
  );
}
